import React, { useState, useRef, useEffect } from "react";

import { Button } from "@aidron/aidron-ds";
import { SYSTEM_COLORS } from "../../system.colors"

export const StableSelect = React.memo(({
  label,
  options = [],
  value,
  onChange,
  placeholder = "Selecione...",
  width = "100%",
  fieldWidth = "6rem",
  disabled = false,
  mode,
  multiple = true,
  showCheckmark = false }) => {

  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const wrapperRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };
    if (isOpen) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  // modo rovitex
  if (mode === 'rovitex') {
    const selectedLabel = options.find(opt => opt.value === value)?.label ?? value;

    const filteredRovitexOptions = options.filter(opt =>
      opt.label?.toString().toLowerCase().includes(searchTerm.toLowerCase())
    );

    const closeRovitex = () => { setIsOpen(false); setSearchTerm(''); };

    return (
      <div ref={wrapperRef} style={{ position: "relative" }}>
        <button onClick={() => setIsOpen(p => !p)} style={{
          minWidth: "120px", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "space-between",
          gap: 8, padding: "6px 12px",
          border: `1px solid ${isOpen ? SYSTEM_COLORS.RED.CORAL_RED : SYSTEM_COLORS.GRAY.GAINSBORO}`, borderRadius: "8px",
          background: SYSTEM_COLORS.WHITE.WHITE, cursor: "pointer", fontSize: "13px", color: SYSTEM_COLORS.GRAY.ONYX,
          outline: "none", whiteSpace: "nowrap" }}>

          <span style={{ whiteSpace: "nowrap" }}>{selectedLabel}</span>
          <svg 
            width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={SYSTEM_COLORS.GRAY.DARK_GRAY} 
            strokeWidth="2.5" style={{ transform: isOpen ? "rotate(180deg)" : "none", 
            transition: "transform 0.15s", flexShrink: 0 }}>
            <polyline points="6 9 12 15 18 9"/>
          </svg>
        </button>

        {isOpen && (
          <div style={{
            position: "absolute", top: "calc(100% + 4px)", left: 0,
            backgroundColor: SYSTEM_COLORS.WHITE.WHITE, border: `1px solid ${SYSTEM_COLORS.GRAY.SILVER}`,
            borderRadius: "10px", boxShadow: `0 4px 16px ${SYSTEM_COLORS.BLACK.BLACK_12_PERCENT}`,
            zIndex: 9999, inlineSize: "100%", minWidth: 220, maxHeight: 320,
            display: "flex", flexDirection: "column" }}>
            <section style={{ padding: "8px 10px", borderBottom: `1px solid ${SYSTEM_COLORS.GRAY.CULTURED}`, flexShrink: 0 }}>
              <div style={{ 
                display: "flex", alignItems: "center", gap: 6, border: `1px solid ${SYSTEM_COLORS.GRAY.PLATINUM}`, 
                borderRadius: "6px", padding: "5px 8px" }}>
                <svg 
                  width={13} height={13} viewBox="0 0 24 24" fill="none" stroke={SYSTEM_COLORS.RED.CORAL_RED}
                  strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="11" cy="11" r="8" />
                  <line x1="21" y1="21" x2="16.65" y2="16.65" />
                </svg>
                <input
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Buscar..."
                  style={{ border: "none", outline: "none", fontSize: "12.5px", width: "100%", background: "none" }}
                  autoFocus
                />
              </div>
            </section>

            <div style={{ overflowY: "auto" }}>
              {filteredRovitexOptions.map((opt, index) => {
                const isActive = opt.value === value;

                return (
                  <button
                    key={opt.value}
                    onClick={() => { onChange(opt.value); closeRovitex(); }}
                    className="stable-select-rovitex-option"
                    style={{
                      width: "100%", display: "flex", alignItems: "center",
                      gap: 10,
                      padding: "9px 14px", background: "none",
                      border: "none", cursor: "pointer", fontSize: "0.813rem",
                      color: SYSTEM_COLORS.BLACK.JET,
                      fontWeight: isActive ? "700" : "400", textAlign: "left",
                      borderBottom: index < filteredRovitexOptions.length - 1 ? `1px solid ${SYSTEM_COLORS.BLACK.BLACK_4_PERCENT}` : "none",
                    }}>
                    {showCheckmark ? (
                      <span style={{ width: 14, display: "inline-flex", flexShrink: 0 }}>
                        {isActive && (
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={SYSTEM_COLORS.RED.CORAL_RED} strokeWidth="2.5">
                            <polyline points="20 6 9 17 4 12"/>
                          </svg>
                        )}
                      </span>
                    ) : (
                      <span style={{
                        width: 7, height: 7, borderRadius: "50%",
                        backgroundColor: isActive ? SYSTEM_COLORS.RED.CORAL_RED : SYSTEM_COLORS.GRAY.SILVER_SAND,
                        display: "inline-block", flexShrink: 0,
                      }} />
                    )}
                    <span>{opt.label}</span>
                  </button>
                );
              })}

              {filteredRovitexOptions.length === 0 && (
                <div style={{ padding: "14px", fontSize: "0.813rem", color: SYSTEM_COLORS.GRAY.GREY, textAlign: "center" }}>
                  Nenhum resultado
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    );
  }

  const normalizedValue = value != null ? Number(value) : "";
  const selectedOption = options.find(opt => Number(opt.value) === normalizedValue);
  const displayText = selectedOption ? `${label} ${selectedOption.label}` : label;
  const filteredOptions = options.filter(opt =>
    opt.label?.toString().includes(searchTerm.toString())
  );

  const handleSelect = (optionValue) => {
    onChange(Number(optionValue));
    setIsOpen(false);
    setSearchTerm('');
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value.toString());
    if (!isOpen) setIsOpen(true);
  };

  return (
    <>
      <select
        className="ragnarok menu-select-hidden"
        value={normalizedValue}
        onChange={(e) => onChange(Number(e.target.value))}
        hidden
      >
        <option value="" disabled>{placeholder}</option>
        {options.map((opt) => (
          <option key={opt.value} value={opt.value}>{opt.label}</option>
        ))}
      </select>

      <div ref={wrapperRef} style={{ width, position: 'relative' }}>
        <div style={{
          display: 'flex', flexDirection: 'row', alignItems: 'center',
          backgroundColor: SYSTEM_COLORS.WHITE.WHITE, borderRadius: '0.6rem',
          borderColor: SYSTEM_COLORS.BLACK.BLACK_12_5_PERCENT, borderStyle: 'solid', borderWidth: '1px',
          boxSizing: 'border-box', color: SYSTEM_COLORS.GRAY.GREY, fontSize: '0.875rem',
          fontWeight: 700, overflow: 'hidden', padding: '0.2em', inlineSize: 'fit-content',
        }}>
          <div style={{
            display: "grid", alignItems: "center", position: "relative",
            inlineSize: fieldWidth, height: "2.2rem", borderRadius: "0.4rem", padding: "0 0.5rem" }}>
            <span className="textfield-label" style={{ position: "absolute", left: "0.5rem", pointerEvents: "none", color: SYSTEM_COLORS.GRAY.GREY }}>
              {displayText}
            </span>
            
            <input
              type="search"
              className="textfield-input"
              value={searchTerm}
              onChange={handleSearchChange}
              onFocus={() => setIsOpen(true)}
              disabled={disabled}
              style={{ 
                width: "100%", height: "100%", background: "transparent", 
                border: "none", outline: "none", color: "transparent", 
                caretColor: "transparent" }}/>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', transform: 'scale(0.7)', transformOrigin: 'center' }}>
            <Button icon={isOpen ? 'arrow-up' : 'arrow-down'} onClick={() => setIsOpen(!isOpen)} disabled={disabled} />
          </div>
        </div>

        {isOpen && (
          <div style={{
            backgroundColor: SYSTEM_COLORS.WHITE.WHITE, borderBottomLeftRadius: '4px', borderBottomRightRadius: '4px',
            boxShadow: `0px 4px 48px 0px ${SYSTEM_COLORS.BLACK.BLACK_16_PERCENT}`, display: 'flex', flexDirection: 'column',
            fontSize: '1rem', maxBlockSize: '240px', inlineSize: '100%', position: 'absolute',
            marginTop: '4px', overflow: 'scroll', zIndex: 999,
          }}>
            {filteredOptions.map((option) => {
              const optionValue = Number(option.value);
              return (
                <button
                  key={option.value}
                  type="button"
                  className={`menu-select-item ${normalizedValue === optionValue ? 'selected-item' : ''}`}
                  onClick={() => handleSelect(option.value)}
                >
                  <span>{normalizedValue === optionValue && '✓'}</span>
                  <span>{option.label}</span>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </>
  );
});

export const ProductHierarchyFilter = ({ ctx, uiState, actions }) => {
  const { detailsByLevel, hierarchy, selectedPaths = [] } = ctx || {};
  const { label = "Produto", placeholder = "Buscar em toda a hierarquia...", align = "left", expandOnSelect = true } = uiState || {};
  const { onChange } = actions || {};

  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [expandedKeys, setExpandedKeys] = useState(new Set());
  const rootRef = useRef(null);
  const buttonRef = useRef(null);

  const levels = hierarchy?.levels ?? [];
  const labels = hierarchy?.labels ?? {};

  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const pathKey = (path) => path.join("\u0000");

  const isPrefix = (prefix, full) => prefix.length <= full.length && prefix.every((seg, i) => full[i] === seg);

  const findGoverningRule = (path) => {
    let best = null;
    selectedPaths.forEach((entry) => {
      if (isPrefix(entry.path, path)) {
        if (!best || entry.path.length > best.path.length) best = entry;
      }
    });
    return best;
  };

  const isSelected = (path) => {
    const rule = findGoverningRule(path);
    if (rule) return !rule.excluded;
    return selectedPaths.some((entry) => !entry.excluded && isPrefix(path, entry.path));
  };

  const getChildren = (levelIndex, parentPath) => {
    const levelKey = levels[levelIndex];
    const entries = detailsByLevel?.[levelKey] ?? [];
    return entries.filter(
      (entry) =>
        entry.path.length === parentPath.length + 1 &&
        parentPath.every((seg, i) => entry.path[i] === seg)
    );
  };

  const collapseSelection = (paths) => {
    let current = paths;
    let changed = true;

    while (changed) {
      changed = false;

      for (const entry of current) {
        if (entry.excluded) continue;

        const parentPath = entry.path;
        const levelIndex = parentPath.length;
        if (levelIndex >= levels.length) continue;

        const children = getChildren(levelIndex, parentPath);
        if (children.length === 0) continue;

        const allChildrenExcluded = children.every((child) =>
          current.some((e) => e.excluded && pathKey(e.path) === pathKey(child.path))
        );

        if (allChildrenExcluded) {
          const childKeys = new Set(children.map((c) => pathKey(c.path)));
          current = current.filter((e) => !childKeys.has(pathKey(e.path)));
          changed = true;
          break;
        }
      }
    }

    return current;
  };

  const toggleSelect = (path) => {
    const key = pathKey(path);
    const rule = findGoverningRule(path);

    const isAncestorOfSelected = !rule && selectedPaths.some(
      (entry) => !entry.excluded && isPrefix(path, entry.path) && pathKey(entry.path) !== key
    );

    let next;

    if (rule && pathKey(rule.path) === key) {
      next = selectedPaths.filter((e) => pathKey(e.path) !== key && !isPrefix(path, e.path));
    } else if (isAncestorOfSelected) {
      next = selectedPaths.filter((e) => !isPrefix(path, e.path));
    } else if (rule) {
      const overrideExcluded = !rule.excluded;
      next = [...selectedPaths, { path, excluded: overrideExcluded }];
      if (!overrideExcluded && expandOnSelect) setExpandedKeys((prev) => new Set(prev).add(key));
    } else {
      next = [...selectedPaths, { path, excluded: false }];
      if (expandOnSelect) setExpandedKeys((prev) => new Set(prev).add(key));
    }

    onChange(collapseSelection(next));
  };

  const toggleExpand = (path) => {
    const key = pathKey(path);
    setExpandedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  const hasChildren = (levelIndex, path) => {
    if (levelIndex + 1 >= levels.length) return false;
    return getChildren(levelIndex + 1, path).length > 0;
  };

  const normalizedTerm = searchTerm.trim().toLowerCase();

  const matchesSearch = (levelIndex, entry) => {
    if (!normalizedTerm) return true;
    if (entry.name?.toString().toLowerCase().includes(normalizedTerm)) return true;
    if (levelIndex + 1 < levels.length) {
      return getChildren(levelIndex + 1, entry.path).some((child) =>
        matchesSearch(levelIndex + 1, child)
      );
    }
    return false;
  };

  useEffect(() => {
    if (!normalizedTerm) return;
    const next = new Set();
    const walk = (levelIndex, parentPath) => {
      getChildren(levelIndex, parentPath).forEach((entry) => {
        if (matchesSearch(levelIndex, entry)) {
          next.add(pathKey(entry.path));
          walk(levelIndex + 1, entry.path);
        }
      });
    };
    walk(0, []);
    setExpandedKeys(next);
  }, [normalizedTerm]);

  const renderLevel = (levelIndex, parentPath) => {
    const levelKey = levels[levelIndex];
    const entries = getChildren(levelIndex, parentPath).filter((entry) =>
      matchesSearch(levelIndex, entry)
    );

    return entries.map((entry) => {
      const key = pathKey(entry.path);
      const expanded = expandedKeys.has(key);
      const selected = isSelected(entry.path);
      const childrenExist = hasChildren(levelIndex, entry.path);

      return (
        <div key={key}>
          <div
            className="phf-row"
            style={{
              display: "flex", alignItems: "center", gap: 8,
              padding: "8px 18px", paddingLeft: 18 + levelIndex * 18,
              border: "1px solid transparent", borderRadius: 8,
            }}
          >
            <span
              onClick={() => childrenExist && toggleExpand(entry.path)}
              style={{ 
                width: 14, display: "inline-flex", justifyContent: "center", 
                cursor: childrenExist ? "pointer" : "default", 
                flexShrink: 0 }}>

              {childrenExist && (
                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke={SYSTEM_COLORS.GRAY.GREY} strokeWidth="3"
                  style={{ transform: expanded ? "rotate(90deg)" : "none", transition: "transform .15s" }}>
                  <polyline points="9 6 15 12 9 18" />
                </svg>
              )}
            </span>

            <span
              onClick={() => toggleSelect(entry.path)}
              style={{
                width: 16, height: 16, borderRadius: 4, flexShrink: 0, cursor: "pointer",
                border: `2px solid ${selected ? SYSTEM_COLORS.RED.CORAL_RED : SYSTEM_COLORS.GRAY.SILVER_SAND}`,
                background: selected ? SYSTEM_COLORS.RED.CORAL_RED : SYSTEM_COLORS.WHITE.WHITE,
                display: "inline-flex", alignItems: "center", justifyContent: "center",
              }}
            >
              {selected && (
                <svg 
                  width={10} height={10} viewBox="0 0 24 24" fill="none" stroke={SYSTEM_COLORS.WHITE.WHITE} 
                  strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="20,6 9,17 4,12" />
                </svg>
              )}
            </span>

            <span
              onClick={() => toggleSelect(entry.path)}
              style={{ fontSize: "0.844rem", color: SYSTEM_COLORS.GRAY.ZINC_700, cursor: "pointer", fontWeight: childrenExist ? 700 : 400, flex: 1 }}
            >
              {entry.name}
            </span>

            <span style={{ fontSize: "0.656rem", color: SYSTEM_COLORS.GRAY.GREY, letterSpacing: 0.3, flexShrink: 0 }}>
              {labels[levelKey] ?? levelKey}
            </span>
          </div>

          {childrenExist && expanded && (
            <div>{renderLevel(levelIndex + 1, entry.path)}</div>
          )}
        </div>
      );
    });
  };

  return (
    <div ref={rootRef} style={{ position: "relative", display: "inline-block" }}>
      <button
        ref={buttonRef}
        onClick={() => setOpen((o) => !o)}
        className="phf-trigger-btn"
        style={{
          display: "flex", alignItems: "center", gap: 8, padding: "8px 16px",
          border: `1px solid ${SYSTEM_COLORS.GRAY.LIGHT_GRAY}`, borderRadius: 12, background: SYSTEM_COLORS.WHITE.WHITE,
          boxShadow: ` 1px 2px ${SYSTEM_COLORS.BLACK.BLACK_4_PERCENT}`, cursor: "pointer", fontSize: "0.813rem", 
          fontWeight: 600, color: SYSTEM_COLORS.GRAY.CHARCOAL, whiteSpace: "nowrap", height: 36 }}>

        <svg 
          width={13} height={13} viewBox="0 0 24 24" fill="none" stroke={SYSTEM_COLORS.GRAY.TAUPE_GRAY} 
          strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="m7.5 4.27 9 5.15" />
          <path d="M21 8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4a2 2 0 0 0 1-1.73Z" />
          <path d="m3.3 7 8.7 5 8.7-5" />
          <path d="M12 22V12" />
        </svg>
                    
        <span style={{ fontSize: "0.844rem", display: "inline-block", paddingBlock: "2rem" }} >{label}</span>

        <svg
          width={11} height={11} viewBox="0 0 24 24" fill="none" stroke={SYSTEM_COLORS.GRAY.ZINC_700}
          strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
          style={{ transform: open ? "rotate(180deg)" : "none", transition: "transform .15s", flexShrink: 0 }}>
          <polyline points="6 9 12 15 18 9" />
        </svg>

        {selectedPaths.length > 0 && (
          <span style={{
            display: "flex", alignItems: "center", justifyContent: "center", width: 18, height: 18,
            borderRadius: "50%", background:SYSTEM_COLORS.RED.CORAL_RED, color: SYSTEM_COLORS.WHITE.WHITE, 
            fontSize: "0.656rem", fontWeight: 700,
          }}>
            {selectedPaths.length}
          </span>
        )}
      </button>

      {open && (
        <div
          style={{
            position: "absolute",
            top: "calc(100% + 4px)",
            [align === "right" ? "right" : "left"]: 0,
            backgroundColor: SYSTEM_COLORS.WHITE.WHITE, border: `1px solid ${SYSTEM_COLORS.GRAY.SILVER}`, borderRadius: 14,
            boxShadow: `0 8px 24px ${SYSTEM_COLORS.BLACK.BLACK_12_PERCENT}`, zIndex: 9999, minWidth: 320,
            maxHeight: 420, display: "flex", flexDirection: "column", overflow: "hidden",
          }}>
          <div style={{ padding: "14px 18px 10px", flexShrink: 0 }}>
            <div style={{ fontSize: "0.75rem", fontWeight: 700, color: SYSTEM_COLORS.GRAY.ZINC_700, letterSpacing: 0.4, marginBottom: 8 }}>
              {label.toUpperCase()} · {levels.map((l) => labels[l] ?? l).join(" → ")}
            </div>
            <div style={{ 
              display: "flex", alignItems: "center", gap: 6, 
              border: `1px solid ${SYSTEM_COLORS.GRAY.PLATINUM}`, 
              borderRadius: 8, padding: "7px 10px" }}>
              <svg 
                width={13} height={13} viewBox="0 0 24 24" fill="none" stroke={SYSTEM_COLORS.RED.CORAL_RED} 
                strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="11" cy="11" r="8" />
                <line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>
              <input
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder={placeholder}
                style={{ border: "none", outline: "none", fontSize: "0.813rem", width: "100%", background: "none" }}
                autoFocus
              />
            </div>
          </div>

          <div style={{ overflowY: "auto", borderTop: `1px solid ${SYSTEM_COLORS.GRAY.CULTURED}` }}>
            {renderLevel(0, [])}
          </div>
        </div>
      )}
    </div>
  );
};

export const FilterDropdown = ({ ctx, uiState, actions }) => {
  const { options, selectedKeys } = ctx || {};
  const { label, icon, variant = "checkbox", align = "left" } = uiState || {};
  const { onChange } = actions || {};
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const toggle = (key) => {
    const isSelected = selectedKeys.includes(key);
    onChange(isSelected ? selectedKeys.filter((k) => k !== key) : [...selectedKeys, key]);
  };

  let lastGroup = null;

  return (
    <div ref={rootRef} style={{ position: "relative", display: "inline-block" }}>
      <button
        onClick={() => setOpen((o) => !o)}
        className="oi-filter-btn-fill"
        style={{
          display: "flex", alignItems: "center", gap: 8, padding: "8px 16px",
          border: `1px solid ${SYSTEM_COLORS.GRAY.LIGHT_GRAY}`, borderRadius: 12, background: SYSTEM_COLORS.WHITE.WHITE,
          boxShadow: `0 1px 2px ${SYSTEM_COLORS.BLACK.BLACK_4_PERCENT}`,
          cursor: "pointer", fontSize: "0.813rem", fontWeight: 600, color: SYSTEM_COLORS.GRAY.CHARCOAL, 
          whiteSpace: "nowrap", height: 36,
        }}>
        {icon}
        <span style={{ fontSize: "0.844rem", display: "inline-block", paddingBlock: "2rem" }}>{label}</span>
        {selectedKeys.length > 0 && (
          <span style={{
            display: "flex", alignItems: "center", justifyContent: "center", width: 18, height: 18,
            borderRadius: "50%", background: SYSTEM_COLORS.ORANGE.BURNST_ORANGE, 
            color: SYSTEM_COLORS.WHITE.WHITE, fontSize: "0.656rem", fontWeight: 700,
          }}>
            {selectedKeys.length}
          </span>
        )}
      </button>

     {open && (
      <div style={{
        position: "absolute", top: "calc(100% + 4px)", [align]: 0, minWidth: 300,
        background: SYSTEM_COLORS.WHITE.WHITE, border: `1px solid ${SYSTEM_COLORS.GRAY.SILVER}`, borderRadius: 14,
        boxShadow: `0 8px 24px ${SYSTEM_COLORS.BLACK.BLACK_12_PERCENT}`, zIndex: 100, padding: "14px 0 10px", maxHeight: 380, overflowY: "auto",
      }}>
        <div style={{ 
          padding: "0 18px 12px", marginBottom: 6, borderBottom: `1px solid ${SYSTEM_COLORS.GRAY.PLATINUM}`, 
          fontSize: "0.813rem", fontWeight: 700, color: SYSTEM_COLORS.GRAY.ZINC_700, letterSpacing: 0.4 }}>
          {label.toUpperCase()}
        </div>

        {options.map((opt) => {
          const showGroupHeader = opt.group && opt.group !== lastGroup;
          const isFirstGroup = options[0].group === opt.group;
          lastGroup = opt.group ?? lastGroup;
          const checked = selectedKeys.includes(opt.key);
          return (
            <div key={opt.key}>
              {showGroupHeader && (
                <div style={{
                  display: "flex", alignItems: "center", gap: 8,
                  padding: "10px 18px 6px", fontSize: 12, fontWeight: 700, color: SYSTEM_COLORS.RED.CORAL_RED, 
                  letterSpacing: 0.5,
                  marginTop: isFirstGroup ? 0 : 4,
                }}>
                <svg width={13} height={13} viewBox="0 0 24 24" fill="none" stroke={SYSTEM_COLORS.RED.CORAL_RED} 
                strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="m12.83 2.18-9.24 3.9a1 1 0 0 0 0 1.84l8.58 3.91a2 2 0 0 0 1.66 0l8.58-3.9a1 1 0 0 0 0-1.84l-8.58-3.9a2 2 0 0 0-1.66 0Z" />
                  <path d="M2 12a1 1 0 0 0 .58.91l8.6 3.91a2 2 0 0 0 1.65 0l8.58-3.9A1 1 0 0 0 22 12" />
                  <path d="M2 17a1 1 0 0 0 .58.91l8.6 3.91a2 2 0 0 0 1.65 0l8.58-3.9A1 1 0 0 0 22 17" />
                </svg>                  
                  {opt.group}
                </div>
              )}
              <div
                onClick={() => toggle(opt.key)}
                className="oi-row-neutral"
                style={{ 
                  display: "flex", alignItems: "center", gap: 12, padding: "10px 18px", 
                  cursor: "pointer", fontSize: "0.938rem", color: SYSTEM_COLORS.GRAY.ZINC_700 }}>
                {variant === "checkbox" ? (
                  <span style={{
                    width: 18, height: 18, borderRadius: 5, flexShrink: 0,
                    border: `2px solid ${checked ? SYSTEM_COLORS.RED.CORAL_RED : SYSTEM_COLORS.GRAY.SILVER_SAND}`, 
                    background: checked ? SYSTEM_COLORS.RED.CORAL_RED : SYSTEM_COLORS.WHITE.WHITE,
                    display: "inline-flex", alignItems: "center", justifyContent: "center",
                  }}>
                    {checked && (
                      <svg 
                        width={10} height={10} viewBox="0 0 24 24" fill="none" stroke={SYSTEM_COLORS.WHITE.WHITE} 
                        strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="20,6 9,17 4,12" />
                      </svg>
                    )}
                  </span>
                ) : (
                  <span style={{
                    width: 15, height: 15, borderRadius: "50%", flexShrink: 0,
                    border: `2px solid ${SYSTEM_COLORS.RED.CORAL_RED}`,
                    background: checked ? SYSTEM_COLORS.RED.CORAL_RED : SYSTEM_COLORS.WHITE.WHITE,
                    display: "inline-flex", alignItems: "center", justifyContent: "center",
                  }} />
                )}
                <span style={{ flex: 1 }}>{opt.label}</span>
              </div>
            </div>
          );
        })}
      </div>
    )}
    </div>
  );
};

export const SortSelectNew = ({ ctx, actions }) => {
  const { value, options } = ctx || {};
  const { onChange } = actions || {};
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    function handler(e) {
      if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  return (
    <div ref={containerRef} style={{ position: "relative", minWidth: 160, display: "inline-block" }}>
      <button
        onClick={() => setOpen((p) => !p)}
        style={{
          width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8,
          padding: "9px 16px", border: `1px solid ${SYSTEM_COLORS.GRAY.GAINSBORO}`, borderRadius: 999,
          background: SYSTEM_COLORS.WHITE.WHITE, cursor: "pointer", fontSize: "0.875rem", color: SYSTEM_COLORS.BLACK.JET,
          outline: "none", whiteSpace: "nowrap",
        }}
      >
        <span>{options.find((o) => o.key === value)?.label}</span>
        <svg 
          width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={SYSTEM_COLORS.GRAY.GREY} 
          strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
          style={{ flexShrink: 0, transform: open ? "rotate(180deg)" : "none", transition: "transform 0.15s" }}>
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>

      {open && (
        <div style={{
          position: "absolute", top: "calc(100% + 4px)", left: 0,
          minWidth: "max(100%, 200px)", backgroundColor: SYSTEM_COLORS.WHITE.WHITE, 
          border: `1px solid ${SYSTEM_COLORS.GRAY.SILVER}`,
          borderRadius: 14, boxShadow: `0 4px 20px ${SYSTEM_COLORS.BLACK.BLACK_12_PERCENT}`, zIndex: 9999,
          maxHeight: 280, overflowY: "auto", padding: "4px 0",
        }}>
          {options.map((opt) => {
            const isActive = opt.key === value;
            return (
              <button
                key={opt.key}
                onClick={() => { onChange(opt.key); setOpen(false); }}
                style={{
                  width: "100%", display: "flex", alignItems: "center", gap: 10, padding: "9px 16px",
                  background: isActive ? SYSTEM_COLORS.WHITE.WHITE_SMOKE : "none", border: "none", cursor: "pointer",
                  fontSize: "0.844rem", color: SYSTEM_COLORS.BLACK.EERIE_BLACK, 
                  fontWeight: isActive ? 600 : 400, textAlign: "left", whiteSpace: "nowrap",
                }}
              >
                <span style={{ width: 16, flexShrink: 0, color: SYSTEM_COLORS.RED.CORAL_RED, fontWeight: 700, fontSize: 14 }}>
                  {isActive ? "✓" : ""}
                </span>
                {opt.label}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};